import { Button } from '@hologram-dimension/button';
import { Modal } from '@hologram-dimension/modal';
import clsx from 'clsx/lite';
import { SimSwitchProfiles } from 'components';
import { useCallback, useEffect, useRef, useState, type FC } from 'react';
import useAnalytics from 'state/contexts/Analytics/useAnalytics';
import type { HologramSimIdsMap, OnButtonClick, OnSubmit, SuccessfulSwitch } from 'types/common';
import type { CommonModalProps } from '../shared';
import styles from './SimSwitchProfiles.module.css';

interface SimSwitchProfilesProps extends CommonModalProps {
  sims: HologramSimIdsMap;
  onSuccess: (sims: Array<SuccessfulSwitch>) => void;
  onButtonClick: OnButtonClick;
}

const SimSwitchProfilesModal: FC<SimSwitchProfilesProps> = ({
  onClose,
  defaultOpen = false,
  className,
  sims,
  onSuccess,
  onButtonClick,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const [error, setError] = useState<string>();
  const [processing, setIsProcessing] = useState(false);
  const submitRef = useRef<OnSubmit>();
  const { sendAnalyticsEvent, sendAnalyticsError } = useAnalytics();

  const onProcessing = useCallback((value: boolean) => {
    setIsProcessing(value);
  }, []);

  const close = useCallback(() => {
    onButtonClick('X');
    setOpen(false);
  }, [onButtonClick]);

  const closeButtonHandler = useCallback(() => {
    onButtonClick('Close');
    setOpen(false);
  }, [onButtonClick]);

  const cancelButtonHandler = useCallback(() => {
    onButtonClick('Cancel');
    setOpen(false);
  }, [onButtonClick]);

  useEffect(() => {
    if (open) {
      if (sendAnalyticsEvent) {
        sendAnalyticsEvent('SIMs - Details - Manage SIM - Switch profile');
      }
    }
  }, [open, sendAnalyticsEvent]);

  const onDone = useCallback(
    (modifiedSims?: Array<SuccessfulSwitch>, switchError?: string) => {
      if (switchError) {
        setError(switchError);
        if (sendAnalyticsError) {
          sendAnalyticsError(switchError);
        }
        return;
      }

      if (modifiedSims) {
        if (sendAnalyticsEvent) {
          sendAnalyticsEvent('SIMs - Details - Manage SIM - Switch profile - Success');
        }
        onSuccess(modifiedSims);
      }
      close();
    },
    [close, onSuccess, sendAnalyticsError, sendAnalyticsEvent]
  );

  const handleSubmit: React.MouseEventHandler &
    React.MouseEventHandler<HTMLAnchorElement> &
    React.MouseEventHandler<HTMLButtonElement> = async (e) => {
    e.preventDefault();

    onButtonClick('Switch profiles');

    if (submitRef.current) {
      await submitRef.current.onSubmit();
    }
  };

  return (
    <Modal
      className={clsx(className, styles.wrapper)}
      header="Switch profiles"
      onClosed={onClose}
      open={open}
      setOpen={setOpen}
      noContentPadding
      footerActions={
        error ? (
          <Button variant="secondary" onClick={closeButtonHandler}>
            Close
          </Button>
        ) : (
          <>
            <Button variant="secondary" onClick={cancelButtonHandler} disabled={processing}>
              Cancel
            </Button>
            <Button loading={processing} onClick={handleSubmit} iconStart="SwapSIM">
              Switch profiles
            </Button>
          </>
        )
      }
    >
      <SimSwitchProfiles sims={sims} ref={submitRef} onDone={onDone} onProcessing={onProcessing} />
    </Modal>
  );
};

export default SimSwitchProfilesModal;
